import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Container, Input, Spinner } from "reactstrap";
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaAngleLeft,
  FaAngleRight,
  FaCheck,
  FaPercent,
  FaTag,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import POSHeader from "./POSHeader";
import "./PromotionPOS.css";
import { categoryActions } from "../../sagas/categorySlice";
import { productActions } from "../../sagas/productSlice";
import { promotionActions } from "../../sagas/promotionSlice";
import PromotionFormModal from "../Promotion/PromotionFormModal";
import { formatPrice } from "../../helpers/utility";
import {
  CPMT_PRODUCTS_PAGE,
  CPMT_RECEPTION_PAGE,
  CPMT_STOCK_PAGE,
  PAY_PAGE,
  POS_PAGE,
  RETOUR_FORM_NEW,
} from "../../routes/routeConstants";

const PromotionListPOS = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { businessName, fullName, commercialId, pointOfSaleId, role } = useSelector((state) => state.auth);
  const { list: categories } = useSelector((state) => state.categories);
  const { unites, products, totalElements, totalPages, pageSize, loading } = useSelector((state) => state.products);
  const { printing } = useSelector((state) => state.promotions);

  const [currentPage, setCurrentPage] = useState(1);
  const [promotionModalOpen, setPromotionModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [activeTab, setActiveTab] = useState("VENTES");
  const [filters, setFilters] = useState({
    categorieId: "",
    query: "",
  });

  const isAdmin = role === "ROLE_ADMIN";

  useEffect(() => {
    dispatch(categoryActions.list({ query: "" }));
    dispatch(productActions.findAllUnites());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      productActions.findAllArticles({
        query: filters.query,
        categorieId: filters.categorieId || null,
        page: currentPage - 1,
        size: 12,
      })
    );
  }, [currentPage, dispatch, filters]);

  const selectedProductList = useMemo(() => Object.values(selectedProducts), [selectedProducts]);

  const initialTickets = useMemo(
    () =>
      selectedProductList.map((product, index) => ({
        articleId: product.id || "",
        articleCode: product.code || "",
        articleLibelle: product.libelle || "",
        prixOriginal: product.prixVenteTTC1 ?? "",
        prixPromo: product.prixVenteTTC1 ?? "",
        barcode: product.codeBarre || "",
        barcodeType: "CODE128",
        quantiteEtiquettes: 1,
        ordreAffichage: index + 1,
      })),
    [selectedProductList]
  );

  const toggleSelectProduct = (product) => {
    setSelectedProducts((prev) => {
      if (prev[product.id]) {
        const next = { ...prev };
        delete next[product.id];
        return next;
      }

      return {
        ...prev,
        [product.id]: product,
      };
    });
  };

  const resetFilters = () => {
    setCurrentPage(1);
    setFilters({ categorieId: "", query: "" });
    setSelectedProducts({});
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "VENTES") navigate(POS_PAGE);
    if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
    if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
    if (tab === "AVANCE") navigate(PAY_PAGE);
    if (tab === "RETOURS") navigate(RETOUR_FORM_NEW);
    if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
  };

  return (
    <Container fluid className="promotion-pos-page">
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        fullName={fullName}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        isAdmin={isAdmin}
        activeTab={activeTab}
        onChangeTab={handleTabChange}
      />

      <div className="promotion-pos-panel">
        <div className="promotion-pos-panel__body">
          <div className="promotion-pos-toolbar">
            <div className="promotion-pos-toolbar__title">
              <h3>Promotions tactiles</h3>
              <p>Selectionnez vos articles puis generez directement vos tickets promo.</p>
            </div>
            <Button
              color="primary"
              className="promotion-pos-primary-btn"
              onClick={() => setPromotionModalOpen(true)}
              disabled={selectedProductList.length === 0 || printing}
            >
              <FaPercent className="me-2" />
              Generer promotion
            </Button>
          </div>

          <div className="promotion-pos-filters">
            <Input
              type="text"
              className="promotion-pos-search"
              placeholder={t("product.searchPlaceholder")}
              value={filters.query}
              onChange={(event) => {
                setCurrentPage(1);
                setFilters((prev) => ({
                  ...prev,
                  query: event.target.value,
                }));
              }}
            />

            <Input
              type="select"
              className="promotion-pos-select"
              value={filters.categorieId}
              onChange={(event) => {
                setCurrentPage(1);
                setFilters((prev) => ({
                  ...prev,
                  categorieId: event.target.value,
                }));
              }}
            >
              <option value="">Toutes les categories</option>
              {(categories || []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.libelle}
                </option>
              ))}
            </Input>

            <Button
              color="light"
              className="promotion-pos-primary-btn"
              onClick={resetFilters}
            >
              Reinitialiser
            </Button>
          </div>

          <div className="promotion-pos-selection-bar">
            <div className="promotion-pos-selection-bar__count">
              {selectedProductList.length} article(s) selectionne(s)
            </div>
            <div className="text-muted">{totalElements || 0} article(s) trouves</div>
          </div>

          {loading ? (
            <div className="promotion-pos-loading">
              <Spinner color="primary" />
              <div className="mt-3">Chargement des articles...</div>
            </div>
          ) : !products || products.length === 0 ? (
            <div className="promotion-pos-empty">
              <FaPercent size={28} className="mb-3 text-muted" />
              <div>Aucun article trouve pour ces filtres.</div>
            </div>
          ) : (
            <div className="promotion-pos-products">
              {products.map((product) => {
                const isSelected = Boolean(selectedProducts[product.id]);
                const uniteLibelle = unites.find((unite) => unite.code === product.unite)?.libelle || "-";

                return (
                  <button
                    key={product.id}
                    type="button"
                    className={`promotion-pos-product ${isSelected ? "is-selected" : ""}`}
                    onClick={() => toggleSelectProduct(product)}
                  >
                    <div className="promotion-pos-product__top">
                      <div>
                        <div className="promotion-pos-product__name">{product.libelle}</div>
                        <div className="promotion-pos-product__category">{product.categorieLibelle || "Sans categorie"}</div>
                      </div>
                      <div className="promotion-pos-product__check">
                        {isSelected ? <FaCheck /> : <span />}
                      </div>
                    </div>

                    <div className="promotion-pos-product__meta">
                      <div className="promotion-pos-product__line">
                        <span>Code</span>
                        <span>{product.code || "-"}</span>
                      </div>
                      <div className="promotion-pos-product__line">
                        <span>Code-barres</span>
                        <span>{product.codeBarre || "-"}</span>
                      </div>
                      <div className="promotion-pos-product__line">
                        <span>Unite</span>
                        <span>{uniteLibelle}</span>
                      </div>
                      <div className="promotion-pos-product__line">
                        <span>
                          <FaTag className="me-2 text-primary" />
                          Prix vente
                        </span>
                        <span>{formatPrice(product.prixVenteTTC1)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="promotion-pos-pagination">
            <div className="text-muted">
              {t("product.showing", {
                start: totalElements === 0 ? 0 : (currentPage - 1) * pageSize + 1,
                end: Math.min(currentPage * pageSize, totalElements),
                total: totalElements,
                currentPage,
                totalPages,
              })}
            </div>
            <div className="promotion-pos-pagination__actions">
              <Button color="light" onClick={() => setCurrentPage(1)} disabled={currentPage <= 1}>
                <FaAngleDoubleLeft />
              </Button>
              <Button color="light" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage <= 1}>
                <FaAngleLeft />
              </Button>
              <Button color="light" onClick={() => setCurrentPage((page) => Math.min(totalPages || 1, page + 1))} disabled={currentPage >= totalPages}>
                <FaAngleRight />
              </Button>
              <Button color="light" onClick={() => setCurrentPage(totalPages || 1)} disabled={currentPage >= totalPages}>
                <FaAngleDoubleRight />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <PromotionFormModal
        isOpen={promotionModalOpen}
        toggle={() => setPromotionModalOpen((value) => !value)}
        initialTickets={initialTickets}
        onCreated={(promotion) => {
          setSelectedProducts({});
          if (!promotion?.id) return;

          dispatch(
            promotionActions.print({
              id: promotion.id,
              onSuccess: () => {},
              onError: () => {},
            })
          );
        }}
      />
    </Container>
  );
};

export default PromotionListPOS;
